/**
 * Machine credentials. The plaintext exists exactly once, in the response that creates
 * it — so this screen shows it once and says so plainly, rather than offering a "reveal"
 * that could never work. Same shape as the Webhooks screen's secret.
 */
import { useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, post } from "../lib/api";
import { stamp } from "../lib/format";
import { maskKey } from "../lib/key";
import type { KeySummary, KeysResponse, NewKeyResponse, Role } from "../lib/types";
import { Shell, Sep } from "../components/Shell";
import { DataTable } from "../components/DataTable";
import { Button, Empty, Failed, Loading, Panel } from "../components/ui";

const ROLES: readonly Role[] = ["owner", "admin", "member", "viewer"] as const;

export function Keys(): ReactElement {
  const queryClient = useQueryClient();
  const [label, setLabel] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const [issued, setIssued] = useState<NewKeyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const listing = useQuery({
    queryKey: ["keys"],
    queryFn: () => get<KeysResponse>("/v1/keys"),
  });

  const mint = useMutation({
    mutationFn: () => post<NewKeyResponse>("/v1/keys", { label: label.trim(), role }),
    onSuccess: (created) => {
      setIssued(created);
      setLabel("");
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["keys"] });
    },
    onError: (cause) => setError(cause instanceof Error ? cause.message : String(cause)),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => del(`/v1/keys/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["keys"] }),
    onError: (cause) => setError(cause instanceof Error ? cause.message : String(cause)),
  });

  return (
    <Shell crumb={<>settings <Sep /> api keys</>} fill>
      {error && (
        <p className="border border-crit/40 bg-crit-wash px-3 py-2 text-[12.5px] text-crit">
          {error}
        </p>
      )}

      <Panel title="Mint a key">
        <form
          className="flex flex-wrap items-end gap-3 p-4"
          onSubmit={(event) => {
            event.preventDefault();
            mint.mutate();
          }}
        >
          <div className="min-w-[260px] flex-1">
            <label htmlFor="key-label" className="eyebrow">
              // label
            </label>
            <input
              id="key-label"
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder="what will use this key"
              className="mt-2 w-full border border-hair bg-ground px-3 py-2 font-mono text-[13px] outline-none focus:border-gold"
            />
          </div>
          <div>
            <label htmlFor="key-role" className="eyebrow">
              // role
            </label>
            <select
              id="key-role"
              value={role}
              onChange={(event) => setRole(event.target.value as Role)}
              className="mt-2 border border-hair bg-ground px-3 py-2 font-mono text-[13px] outline-none focus:border-gold"
            >
              {ROLES.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <Button variant="primary" type="submit" disabled={mint.isPending}>
            mint key
          </Button>
        </form>

        {issued && (
          <div className="border-t border-hair px-4 py-3">
            <p className="eyebrow">// {issued.label}</p>
            <code
              data-testid="new-key"
              className="mt-2 block break-all border border-hair bg-ground px-3 py-2 font-mono text-[12px]"
            >
              {issued.key}
            </code>
            <div className="mt-2 flex items-center gap-3">
              <Button onClick={() => void navigator.clipboard?.writeText(issued.key)}>
                copy
              </Button>
              <span className="text-[12px] text-dim">
                This is the only time it is shown. A lost key is reissued, not recovered.
              </span>
            </div>
          </div>
        )}
      </Panel>

      <Panel title="Keys" fill>
        {listing.isPending ? (
          <Loading what="keys" />
        ) : listing.error ? (
          <Failed error={listing.error} what="keys" />
        ) : (listing.data?.keys.length ?? 0) === 0 ? (
          <Empty title="No keys" hint="Mint one above for a script or an integration." />
        ) : (
          <DataTable
            fill
            table="keys"
            rows={listing.data?.keys ?? []}
            rowKey={(key: KeySummary) => key.id}
            columns={[
              { key: "label", header: "Label", width: 220, render: (k) => k.label },
              {
                key: "id",
                header: "Key",
                width: 200,
                cellClass: "font-mono",
                render: (k) => maskKey(`lrd_${k.id}_${k.id.slice(-4)}`),
              },
              { key: "role", header: "Role", width: 120, render: (k) => k.role },
              {
                key: "created",
                header: "Created",
                width: 180,
                cellClass: "font-mono",
                render: (k) => stamp(k.created_at),
              },
              {
                key: "state",
                header: "State",
                width: 200,
                cellClass: "font-mono",
                render: (k) =>
                  // A revoked key stays listed: hiding it makes "was this credential ever
                  // real" unanswerable.
                  k.revoked_at ? `revoked ${stamp(k.revoked_at)}` : "active",
              },
              {
                key: "actions",
                header: "",
                width: 110,
                align: "end",
                render: (k) =>
                  k.revoked_at ? null : (
                    <Button onClick={() => revoke.mutate(k.id)}>revoke</Button>
                  ),
              },
            ]}
          />
        )}
      </Panel>
    </Shell>
  );
}
